/**
 * Calls for "Test stream" (ENG-2470), through `createApi` like the rest of
 * the app, with the session-refresh wrapper rather than the generic error
 * handler: every "no" admin gives (a test already running, a rate limit, the
 * test switched off) is explained in words by the panel.
 */
import { AxiosError } from "axios";
import { createApi } from "../../../utils/createApi";
import { PERMISSION_DENIED_MESSAGES } from "../../../utils/universalAPIErrorHandler";
import { withSessionRecovery } from "../../Troubleshooting/TroubleshootingUtils/authRecovery";
import {
  plainDetail,
  retryAtFrom,
} from "../SiteConnectivity/siteConnectivityApi";
import { formatClock } from "../SiteConnectivity/connectivityModel";
import {
  StreamTest,
  StreamTestOutcome,
  StreamTestStarted,
} from "./streamTestTypes";

export const STREAM_TEST_PATH = "camera-stream-test/";
export const streamTestPath = (testId: string) =>
  `camera-stream-test/${encodeURIComponent(testId)}/`;
export const latestStreamTestPath = (cameraId: number) =>
  `camera-stream-test/latest/?camera_id=${cameraId}`;

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === "object" ? (value as Record<string, unknown>) : {};

const text = (value: unknown): string | null =>
  typeof value === "string" && value ? value : null;

/** admin's 503 for email and partner-event sites names what they send. */
const SITE_TYPE_DETAIL = /\b(e-?mails?|partner events?)\b/i;

export const streamTestOutcomeFromError = <T>(
  error: unknown
): StreamTestOutcome<T> => {
  const axiosError = error as AxiosError;
  const status = axiosError?.response?.status;
  const body = asRecord(axiosError?.response?.data);
  const detail = text(body.detail);
  if (status === 404) return { kind: "not_found" };
  if (status === 405 || status === 501 || status === 503) {
    return {
      kind: "unavailable",
      siteType: Boolean(detail && SITE_TYPE_DETAIL.test(detail)),
      detail,
    };
  }
  if (status === 400 || status === 409 || status === 429) {
    return {
      kind: "refused",
      code:
        text(body.code) ??
        (status === 409
          ? "test_active"
          : status === 429
          ? "rate_limited"
          : "rejected"),
      detail,
      runId: text(body.run_id),
      retryAt: status === 429 ? retryAtFrom(axiosError) : null,
    };
  }
  if (status === 401 || status === 403) {
    return {
      kind: "forbidden",
      message:
        PERMISSION_DENIED_MESSAGES[detail ?? ""] ||
        "You don't have permission to test this camera. Ask your administrator if you need access.",
    };
  }
  if (status != null && status >= 500) {
    return {
      kind: "error",
      message: `The server couldn't answer (error ${status}). Try again in a minute.`,
    };
  }
  return {
    kind: "error",
    message:
      "We couldn't reach the server. Check your internet connection and try again.",
  };
};

const run = async <T>(
  request: () => Promise<{ data: T }>
): Promise<StreamTestOutcome<T>> => {
  try {
    const response = await withSessionRecovery(request);
    const body = asRecord(response.data);
    if (body.status === "unavailable") {
      const detail = text(body.detail);
      return {
        kind: "unavailable",
        siteType: Boolean(detail && SITE_TYPE_DETAIL.test(detail)),
        detail,
      };
    }
    return { kind: "ok", data: response.data };
  } catch (error) {
    return streamTestOutcomeFromError<T>(error);
  }
};

/** The connector's internal deployment name never reaches the customer. */
const plainTest = (
  outcome: StreamTestOutcome<StreamTest>
): StreamTestOutcome<StreamTest> =>
  outcome.kind === "ok"
    ? {
        ...outcome,
        data: {
          ...outcome.data,
          detail: plainDetail(outcome.data.detail),
          result: outcome.data.result
            ? {
                ...outcome.data.result,
                detail: plainDetail(outcome.data.result.detail),
              }
            : null,
        },
      }
    : outcome;

/** Start a test. 202 → `{test_id, status}`. */
export const startStreamTest = async (
  cameraId: number
): Promise<StreamTestOutcome<StreamTestStarted>> =>
  run(() =>
    createApi("").post<StreamTestStarted>(STREAM_TEST_PATH, {
      camera_id: cameraId,
    })
  );

export const fetchStreamTest = async (
  testId: string
): Promise<StreamTestOutcome<StreamTest>> =>
  plainTest(
    await run(() => createApi("").get<StreamTest>(streamTestPath(testId)))
  );

/** The camera's most recent test; `not_found` when it has never had one. */
export const fetchLatestStreamTest = async (
  cameraId: number
): Promise<StreamTestOutcome<StreamTest>> =>
  plainTest(
    await run(() =>
      createApi("").get<StreamTest>(latestStreamTestPath(cameraId))
    )
  );

export const STREAM_TEST_OFF_TEXT = "Stream test isn't available yet.";
export const STREAM_TEST_SITE_TYPE_TEXT =
  "Stream test isn't available for this site type";

const ADDRESS_NOT_ALLOWED_TEXT =
  "This camera's saved address is a private address that isn't on one of this site's WireGuard tunnels or Site-to-Site VPNs, so we can't reach it from our side. Check the camera's address, or connect the site's tunnel.";

const secondsUntil = (iso: string, now: number): number =>
  Math.max(1, Math.ceil((Date.parse(iso) - now) / 1000));

/** A refusal, in words, with when to try again where admin says. */
export const refusalText = (
  refusal: { code: string; detail: string | null; retryAt: string | null },
  now: number = Date.now()
): string => {
  const { code, detail, retryAt } = refusal;
  const at = retryAt ? formatClock(retryAt, new Date(now)) : null;
  switch (code) {
    case "test_active":
      return "This camera is already being tested. Wait for the result.";
    case "camera_rate_limited":
      return `This camera was tested a moment ago. ${
        retryAt
          ? `You can test it again in ${secondsUntil(retryAt, now)} s.`
          : "Try again in a few seconds."
      }`;
    case "site_rate_limited":
      return `This site's cameras have been tested many times in the last hour. ${
        at ? `You can test again at ${at}.` : "Try again later."
      }`;
    case "user_rate_limited":
      return `You've run many stream tests in the last hour. ${
        at ? `You can run another at ${at}.` : "Try again later."
      }`;
    case "cluster_busy":
      return `Our test capacity is busy right now. ${
        at ? `Try again at ${at}.` : "Try again in a few minutes."
      }`;
    case "camera_inactive":
      return "This camera is turned off, so there's no stream to test.";
    case "address_not_allowed":
      return detail ?? ADDRESS_NOT_ALLOWED_TEXT;
    case "not_supported":
      return "Stream test isn't available for this site.";
    default:
      return detail ?? "We couldn't start the stream test. Try again later.";
  }
};

/** How our side reached the camera, in plain words. */
export const NETWORK_PATH_TEXT: Record<string, string> = {
  default_path: "from our servers' address",
  wireguard: "through your WireGuard tunnel",
  vpn: "through your site-to-site VPN",
};

export const networkPathText = (path: string | null | undefined): string =>
  (path && NETWORK_PATH_TEXT[path]) || NETWORK_PATH_TEXT.default_path;
